import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAuthContext } from '../../auth/AuthContext';
import { prepareScsObjectivesShare, sendScsObjectivesEmail } from '../../service/form';
import { blobToBase64 } from '../../utils/base64-utils';
import { parseEmailList } from '../../utils/email-list-utils';
import { createScsObjectivesPdf } from '../../utils/scs-objectives-pdf';

type Props = {
  answers: Record<string, unknown>;
  buttonText: string;
  disabled?: boolean;
  emailFieldName: string;
  formId?: string;
  formVersion?: number;
  onSentEmailsChange: (emails: string[]) => void;
  statusFieldName: string;
};

const ScsObjectivesShareAction = ({
  answers,
  buttonText,
  disabled,
  emailFieldName,
  formId,
  formVersion,
  onSentEmailsChange,
  statusFieldName,
}: Props) => {
  const { fetchJWT } = useAuthContext();
  const { clearErrors, getValues, setError, setValue, trigger, watch } = useFormContext();
  const emailValue = String(watch(emailFieldName) ?? '');
  const shareStatus = String(watch(statusFieldName) ?? '').trim();
  const normalizedEmailValue = parseEmailList(emailValue).normalizedValue;
  const originalEmailValue = useRef(normalizedEmailValue);
  const shareToken = useRef('');
  const sentEmails = useRef(new Set<string>());
  const [isSharing, setIsSharing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, email: '' });
  const [success, setSuccess] = useState(Boolean(shareStatus));
  const [shareError, setShareError] = useState('');

  const publishSentEmails = () => {
    onSentEmailsChange(Array.from(sentEmails.current));
  };

  useEffect(() => {
    if (!shareStatus) return;
    sentEmails.current = new Set(
      parseEmailList(emailValue)
        .entries.filter(({ isValid }) => isValid)
        .map(({ value }) => value.toLowerCase()),
    );
    publishSentEmails();
    setSuccess(true);
  }, []);

  useEffect(() => {
    if (normalizedEmailValue === originalEmailValue.current) return;
    originalEmailValue.current = normalizedEmailValue;
    shareToken.current = '';
    sentEmails.current.clear();
    publishSentEmails();
    setSuccess(false);
    setShareError('');
  }, [normalizedEmailValue]);

  const shareObjectives = async () => {
    setShareError('');
    clearErrors(statusFieldName);

    const emailIsValid = await trigger(emailFieldName);
    const parsed = parseEmailList(emailValue);
    if (
      !emailIsValid ||
      parsed.hasInvalidEntries ||
      parsed.entries.every(({ isValid }) => !isValid)
    )
      return;
    if (!formId) {
      setShareError('Unable to identify this form. Please refresh and try again.');
      return;
    }

    const recipients = parsed.entries
      .filter(({ isValid }) => isValid)
      .map(({ value }) => value.toLowerCase());
    const recipientsToSend = recipients.filter((email) => !sentEmails.current.has(email));
    if (recipientsToSend.length === 0 && recipients.length > 0) return;

    setIsSharing(true);
    setProgress({
      current: recipients.indexOf(recipientsToSend[0]) + 1,
      total: recipients.length,
      email: recipientsToSend[0],
    });

    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      if (!shareToken.current) {
        const { blob } = await createScsObjectivesPdf(token, answers, formVersion, formId);
        const prepared = await prepareScsObjectivesShare(token, await blobToBase64(blob), formId);
        shareToken.current = prepared.share_token;
      }

      let lastSentAt = '';
      let failedCount = 0;
      for (let index = 0; index < recipientsToSend.length; index += 1) {
        const recipient = recipientsToSend[index];
        setProgress({
          current: recipients.indexOf(recipient) + 1,
          total: recipients.length,
          email: recipient,
        });
        try {
          const result = await sendScsObjectivesEmail(token, recipient, shareToken.current, formId);
          sentEmails.current.add(recipient);
          lastSentAt = result.sent_at;
          publishSentEmails();
        } catch (error) {
          failedCount += 1;
          console.error(`SCS objectives email failed for ${recipient}`, error);
        }
      }

      if (failedCount === 0 && sentEmails.current.size === recipients.length) {
        const existingShareStatus = String(getValues(statusFieldName) ?? '').trim();
        if (!existingShareStatus) {
          setValue(statusFieldName, lastSentAt, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }
        clearErrors(statusFieldName);
        setSuccess(true);
      } else {
        const message = 'Some objectives emails could not be sent. Try again.';
        if (!String(getValues(statusFieldName) ?? '').trim()) {
          setValue(statusFieldName, '', { shouldDirty: true, shouldValidate: false });
        }
        setError(statusFieldName, { type: 'manual', message });
      }
    } catch (error) {
      console.error('SCS objectives sharing failed', error);
      setShareError('Unable to share objectives right now. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <>
      <button
        className="govuk-button"
        type="button"
        data-module="govuk-button"
        onClick={() => void shareObjectives()}
        disabled={disabled || isSharing || success}
      >
        {buttonText}
      </button>
      {success && (
        <p className="govuk-body scs-objectives-share-success" role="status">
          Objectives have been shared via email
        </p>
      )}
      {shareError && (
        <p className="govuk-error-message" role="alert">
          {shareError}
        </p>
      )}
      {isSharing && (
        <div
          className="scs-objectives-share-overlay"
          role="status"
          aria-live="polite"
          aria-label="Sharing objectives"
        >
          <div>
            <div className="scs-objectives-share-spinner" />
            <p className="govuk-body scs-objectives-share-progress">
              Sharing objectives ({progress.current} of {progress.total}): {progress.email}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ScsObjectivesShareAction;
